import { useEffect, useState } from "react";
import { computeBonuses, type RunOutcome } from "../game/types";
import { Fireworks } from "./Fireworks";
import { PodiumScene } from "./PodiumScene";
import type { AvatarConfig } from "@shared/types";

interface Props {
  outcome: RunOutcome;
  hasNext: boolean;
  onPlayAgain: () => void;
  onNext: () => void;
  onHome: () => void;
  /** Avatar of the player; drives the dancing podium scene at the top. */
  avatar?: AvatarConfig;
}

export function ResultsScreen({
  outcome,
  hasNext,
  onPlayAgain,
  onNext,
  onHome,
  avatar,
}: Props) {
  const bonus = computeBonuses(outcome);
  const correct = outcome.wordOutcomes.filter((w) => w.correct).length;
  const total = outcome.wordOutcomes.length;
  const seconds = Math.round(outcome.timeMs / 1000);

  // Animated counters — coins tick up over ~900ms.
  const [shownCoins, setShownCoins] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const dur = 900;
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setShownCoins(Math.round(bonus.totalCoins * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [bonus.totalCoins]);

  return (
    <div className="results-overlay results-victory-overlay">
      {outcome.finished && <Fireworks />}
      <div className={`results-card results-victory-card ${bonus.flawless ? "flawless" : ""}`}>
        <div className="results-finished-title" aria-label={outcome.finished ? "Finished" : "Try again"}>
          {outcome.finished ? "Finished" : "Try Again"}
        </div>

        {avatar && <PodiumScene avatar={avatar} flawless={bonus.flawless} />}

        <div className="results-victory-stats" aria-label="Run summary">
          <div className="results-stat-pill">
            <span>Words</span>
            <strong>{correct}/{total}</strong>
          </div>
          <div className="results-stat-pill">
            <span>Time</span>
            <strong>{seconds}s</strong>
          </div>
          <div className="results-stat-pill">
            <span>Coins</span>
            <strong>{shownCoins}</strong>
          </div>
        </div>

        {bonus.flawless && <div className="results-perfect-badge">Perfect run</div>}

        <div className="results-actions results-victory-actions">
          <button className="btn-secondary results-play-again" onClick={onPlayAgain}>
            Play again
          </button>
          {outcome.finished && hasNext ? (
            <button className="btn-primary btn-big" onClick={onNext}>
              Next level
            </button>
          ) : (
            <button className="btn-primary btn-big" onClick={onHome}>
              Home
            </button>
          )}
        </div>
        <button className="results-home-link" onClick={onHome}>
          Back to lessons
        </button>
      </div>
    </div>
  );
}
